import React, { useRef, useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import {
  Download,
  PersonOffOutlined,
  PersonOutlined,
  History,
  VisibilityOutlined,
  Business,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DataTable from '@/components/DataTable/DataTable';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import { useConfirmation } from '@/context/ConfirmationContext';
import { useToggleFacilityUser } from '@/api/hooks/users/useToggleFacilityUser';
import { useGetRolesForForms } from '@/api/hooks/roles/useGetRolesForForms';
import InternalUserHistory from './InternalUserHistory';
import RoleChangeDialog from './RoleChangeDialog';
import AssignFacilitiesDialog from './AssignFacilitiesDialog';
import EmployeeInfoDialog from '@/views/Facilities/UserManagement/components/EmployeeInfoDialog';
import CheckPermissionsComponent, {
  checkPermission,
} from '@/views/Facilities/CheckPermissions';
import useCurrentOrganization from '@/hooks/useCurrentOrganization';
import FacilityAccessChips from '@/components/FacilityAccessChips/FacilityAccessChips';
import { useExportFacilityUsers } from '@/api/hooks/users/useExportFacilityUsers';
import FacilityUserRoleColumn from '@/components/FacilityUserRoleColumn/FacilityUserRoleColumn';
import FacilityUserStatusChip from '@/components/FacilityUserStatusChip/FacilityUserStatusChip';
import { USER_STATUS } from '@/views/Facilities/UserManagement/components/userHistory.utils';

const InternalUserManagement = () => {
  const { t } = useTranslation();
  const { requestConfirmation } = useConfirmation();
  const { id: organizationId } = useCurrentOrganization();
  const dataTableRef = useRef(null);

  const { enableUser, disableUser } = useToggleFacilityUser();
  const { roles } = useGetRolesForForms();
  const { exportUsers, isExporting } = useExportFacilityUsers();

  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [employeeInfoDialogOpen, setEmployeeInfoDialogOpen] = useState(false);
  const [assignFacilitiesDialogOpen, setAssignFacilitiesDialogOpen] = useState(
    false,
  );
  const [selectedUser, setSelectedUser] = useState(null);

  const handleToggleUser = (user, disable) => {
    const action = disable
      ? t('internalUsers.disable')
      : t('internalUsers.enable');
    const confirmMessage = t('internalUsers.confirmToggle', {
      action,
      name: user.name,
    });

    requestConfirmation(confirmMessage, () =>
      confirmToggleUser(user.id, disable),
    );
  };

  const confirmToggleUser = async (userId, disable) => {
    try {
      if (disable) {
        await disableUser(userId);
      } else {
        await enableUser(userId);
      }
      dataTableRef.current?.refresh();
    } catch (error) {
      console.error('Error toggling user:', error);
    }
  };

  const handleViewHistory = (user) => {
    setSelectedUser(user);
    setHistoryDialogOpen(true);
  };

  const handleViewEmployeeInfo = (user) => {
    setSelectedUser(user);
    setEmployeeInfoDialogOpen(true);
  };

  const handleChangeRole = (user) => {
    setSelectedUser(user);
    setRoleDialogOpen(true);
  };

  const handleRoleChanged = () => {
    dataTableRef.current?.refresh();
  };

  const handleAssignFacilities = (user) => {
    setSelectedUser(user);
    setAssignFacilitiesDialogOpen(true);
  };

  const handleFacilitiesAssigned = () => {
    dataTableRef.current?.refresh();
  };

  const handleExportUsers = () => {
    exportUsers({ organization_id: organizationId });
  };

  const formatLastSignIn = (dateString) => {
    if (!dateString) return t('internalUsers.never');

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const canWrite = checkPermission({
    parentKey: 'facility_user_management',
    childKey: 'facility_user_management',
    permission: 'write',
  });

  const canAssignFacilities = checkPermission({
    parentKey: 'facility_user_management',
    childKey: 'assign_user_facilities',
    permission: 'write',
  });

  const columns = [
    {
      key: 'name',
      label: t('internalUsers.columns.name'),
      sortable: true,
      render: (value) => (
        <Box>
          <Typography variant='body2' fontWeight={500}>
            {value}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'email',
      label: t('internalUsers.columns.email'),
      sortable: true,
      render: (value) => (
        <Typography variant='body2' color='text.secondary'>
          {value}
        </Typography>
      ),
    },
    {
      key: 'role_name',
      label: t('internalUsers.columns.role'),
      sortable: true,
      render: (_value, row) => (
        <FacilityUserRoleColumn user={row} onChangeRole={handleChangeRole} />
      ),
    },
    {
      key: 'facilities',
      label: t('internalUsers.columns.facilities'),
      sortable: false,
      render: (value, row) => (
        <FacilityAccessChips
          facilities={value}
          hasAllFacilitiesAccess={row.has_all_facilities_access}
        />
      ),
    },
    {
      key: 'last_sign_in_at',
      label: t('internalUsers.columns.lastSignIn'),
      sortable: true,
      render: (value) => (
        <Typography variant='body2' color='text.secondary'>
          {formatLastSignIn(value)}
        </Typography>
      ),
    },
    {
      key: 'status',
      label: t('internalUsers.columns.status'),
      sortable: true,
      render: (value) => <FacilityUserStatusChip status={value} />,
    },
    {
      key: 'actions',
      label: t('internalUsers.columns.actions'),
      sortable: false,
      render: (_value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={t('employeeInfo.viewTooltip')}>
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handleViewEmployeeInfo(row);
              }}>
              <VisibilityOutlined fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('internalUsers.viewHistory')}>
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handleViewHistory(row);
              }}>
              <History fontSize='small' />
            </IconButton>
          </Tooltip>

          {canAssignFacilities && row.status === USER_STATUS.ONBOARDING && (
            <Tooltip title={t('internalUsers.assignFacilities')}>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  handleAssignFacilities(row);
                }}>
                <Business color='primary' fontSize='small' />
              </IconButton>
            </Tooltip>
          )}

          {canWrite && (
            <>
              {row.status === USER_STATUS.ACTIVE ? (
                <Tooltip title={t('internalUsers.disableUser')}>
                  <IconButton
                    size='small'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleUser(row, true);
                    }}>
                    <PersonOffOutlined color='warning' fontSize='small' />
                  </IconButton>
                </Tooltip>
              ) : row.status === USER_STATUS.INACTIVE ? (
                <Tooltip title={t('internalUsers.enableUser')}>
                  <IconButton
                    size='small'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleUser(row, false);
                    }}>
                    <PersonOutlined color='success' fontSize='small' />
                  </IconButton>
                </Tooltip>
              ) : null}
            </>
          )}
        </Box>
      ),
    },
  ];

  const columnFilters = [
    {
      key: 'search',
      label: t('internalUsers.filters.search'),
      filterType: 'search',
      placeholder: t('internalUsers.filters.searchPlaceholder'),
      debounce: true,
      debounceDelay: 500,
    },
    {
      key: 'role_id',
      label: t('internalUsers.filters.role'),
      filterType: 'select',
      options: [
        { value: '', label: t('internalUsers.filters.allRoles') },
        ...roles.map((role) => ({ value: role.id, label: role.name })),
      ],
    },
    {
      key: 'status',
      label: t('internalUsers.filters.status'),
      filterType: 'select',
      options: [
        { value: '', label: t('facilityUserManagement.status.allUsers') },
        {
          value: String(USER_STATUS.ACTIVE),
          label: t('facilityUserManagement.status.active'),
        },
        {
          value: String(USER_STATUS.INACTIVE),
          label: t('facilityUserManagement.status.inactive'),
        },
        {
          value: String(USER_STATUS.ONBOARDING),
          label: t('facilityUserManagement.status.onboarding'),
        },
      ],
    },
  ];

  return (
    <CheckPermissionsComponent
      parentKey='facility_user_management'
      childKey='viewPage'
      permission='read'>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color='primary'>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                <h4 style={{ margin: 0 }}>{t('internalUsers.pageTitle')}</h4>
                <Tooltip
                  title={t('facilityUserManagement.exportTooltip')}
                  arrow>
                  <span>
                    <Button
                      startIcon={<Download sx={{ color: 'white' }} />}
                      onClick={handleExportUsers}
                      disabled={isExporting}
                      sx={{
                        color: 'white',
                        borderColor: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderColor: 'white',
                        },
                      }}
                      variant='outlined'>
                      {isExporting
                        ? t('common.exporting')
                        : t('facilityUserManagement.exportButton')}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </CardHeader>
            <CardBody>
              <DataTable
                ref={dataTableRef}
                columns={columns}
                endpoint='/users/facility'
                initialSort={{ key: 'name', order: 'desc' }}
                pageSize={50}
                enableColumnPicker={true}
                enableSorting={true}
                enableFilters={true}
                persistenceKey='internal-users'
                enableUrlPersistence={true}
                columnFilters={columnFilters}
                emptyStateMessage={t('internalUsers.noUsers')}
                initialFilters={{ organization_id: organizationId }}
              />
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {selectedUser && (
        <InternalUserHistory
          open={historyDialogOpen}
          onClose={() => setHistoryDialogOpen(false)}
          user={selectedUser}
        />
      )}

      {selectedUser && (
        <RoleChangeDialog
          open={roleDialogOpen}
          onClose={() => setRoleDialogOpen(false)}
          user={selectedUser}
          onRoleChanged={handleRoleChanged}
        />
      )}

      {selectedUser && (
        <EmployeeInfoDialog
          open={employeeInfoDialogOpen}
          onClose={() => setEmployeeInfoDialogOpen(false)}
          user={selectedUser}
        />
      )}

      {selectedUser && (
        <AssignFacilitiesDialog
          open={assignFacilitiesDialogOpen}
          onClose={() => setAssignFacilitiesDialogOpen(false)}
          user={selectedUser}
          onSuccess={handleFacilitiesAssigned}
        />
      )}
    </CheckPermissionsComponent>
  );
};

export default InternalUserManagement;
